import { Box, Skeleton, Table, TableBody, TableContainer, TableHead } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { PAGE_SIZE } from 'services/constants/env';
import { ItemWithId } from 'services/constants/types';
import { EasTableHeader } from './EasTableHeader';
import { EasRow } from './EasRow';
import { TEasTableProps } from './types';
import { COLORS } from 'services/constants/colors';

const ROWS = Array.from(new Array(PAGE_SIZE / 2));

export const EasTable = <T extends ItemWithId>({
	tableStructure,
	items,
	itemsIsLoading,
	handleAction,
	handleLink,
	skeletonRows = ROWS,
	highlightedRow,
	name,
	showButtons,
}: TEasTableProps<T>) => {
	const [selectedRow, setSelectedRow] = useState<number | null>(null);

	const [firstRender, setFirstRender] = useState(true);

	useEffect(() => {
		if (firstRender) {
			setFirstRender(false);
		}
	}, [firstRender]);

	return (
		<Box>
			<TableContainer>
				<Table onMouseLeave={() => setSelectedRow(null)} onBlur={() => setSelectedRow(null)}>
					<TableHead>
						<EasTableHeader tableStructure={tableStructure} />
					</TableHead>
					{!!items?.length && (
						<TableBody
							onMouseLeave={() => setSelectedRow(null)}
							onBlur={() => setSelectedRow(null)}
						>
							{!!items &&
								items?.map((item, index) => (
									<EasRow<T>
										key={item.id}
										item={item}
										setSelectedRow={setSelectedRow}
										selectedRow={selectedRow}
										highlightedRow={highlightedRow}
										index={index}
										tableStructure={tableStructure}
										handleAction={handleAction}
										handleLink={handleLink}
										showButtons={showButtons?.[index]}
									/>
								))}
						</TableBody>
					)}
				</Table>
				{itemsIsLoading && !items?.length && (
					<Box
						sx={{
							pb: '1px',
						}}
					>
						{skeletonRows?.map(() => (
							<Skeleton
								key={nanoid()}
								variant="rectangular"
								height="33px"
								sx={{
									mb: '33px',
								}}
							/>
						))}
					</Box>
				)}
				{!itemsIsLoading && !items?.length && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: skeletonRows.length * 33 + 33,
							width: '100%',
							color: COLORS.SECONDARY,
							fontSize: '24px',
							fontWeight: 'bold',
						}}
					>
						No {name ? `${name}s` : 'items'} found...
					</Box>
				)}
			</TableContainer>
		</Box>
	);
};
